import { useState } from 'react';
import { Star, Send } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RatingFeedback = ({ analysisId, darkMode = false }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setLoading(true);
    try {
      console.log('📤 Submitting feedback:', { analysisId, rating, feedback: feedback.trim() });
      const response = await axios.post(`${API}/feedback`, {
        analysis_id: analysisId,
        rating: rating,
        feedback_text: feedback.trim() || null
      }, {
        withCredentials: true,
        timeout: 15000 // 15 second timeout
      });
      
      console.log('✅ Feedback response:', response.data);
      toast.success('Thank you for your feedback!');
      setSubmitted(true);
    } catch (error) {
      console.error('❌ Feedback submission error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      if (error.code === 'ECONNABORTED') {
        toast.error('Request timeout - please try again');
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.detail || 'Invalid feedback data');
      } else {
        toast.error('Failed to submit feedback - please try again');
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Card className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-green-50 border-green-200'}`}>
        <CardContent className="py-6 text-center">
          <div className="text-4xl mb-2">✅</div>
          <p className={`font-semibold ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
            Thank you for your feedback!
          </p>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Your input helps us improve emoQ.ai
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <CardContent className="py-6">
        <div className="text-center mb-4">
          <h3 className={`text-lg font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Rate This Analysis
          </h3>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Help us improve by sharing your experience
          </p>
        </div>

        {/* Star Rating */}
        <div className="flex justify-center gap-2 mb-4">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHover(star)}
              onMouseLeave={() => setHover(0)}
              className="transition-transform hover:scale-110 focus:outline-none"
            >
              <Star
                className={`w-8 h-8 ${
                  star <= (hover || rating)
                    ? 'fill-yellow-400 text-yellow-400'
                    : darkMode
                    ? 'text-gray-600'
                    : 'text-gray-300'
                }`}
              />
            </button>
          ))}
        </div>

        {/* Rating Labels */}
        {rating > 0 && (
          <p className={`text-center text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {rating === 1 && 'Poor'}
            {rating === 2 && 'Fair'}
            {rating === 3 && 'Good'}
            {rating === 4 && 'Very Good'}
            {rating === 5 && 'Excellent'}
          </p>
        )}

        {/* Feedback Text */}
        <div className="mb-4">
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Share your thoughts (optional)"
            rows={3}
            className={`w-full px-3 py-2 rounded-lg border resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              darkMode
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
            maxLength={500}
          />
          <p className={`text-xs text-right mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {feedback.length}/500
          </p>
        </div>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={loading || rating === 0}
          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Submit Feedback
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default RatingFeedback;
